import { BodyText } from '@/components/BodyText';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MergeIcon from '@mui/icons-material/Merge';
import DeleteIcon from '@mui/icons-material/Delete';
import { GroupNameContainer } from './ReportSample.styles';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useState } from 'react';

export const GroupNameDisplay = ({
  groupName,
  onEdit,
  onMerge,
  onDelete,
  contextMenuAnchor,
  onContextMenuClose,
}: {
  groupName: string;
  onEdit: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMerge?: () => void;
  onDelete?: () => void;
  contextMenuAnchor?: { mouseX: number; mouseY: number };
  onContextMenuClose?: () => void;
}) => {
  const { permissions } = useGetCurrentUser();
  const [menuAnchorElement, setMenuAnchorElement] = useState<undefined | HTMLElement>();

  const isMenuOpen = Boolean(menuAnchorElement) || Boolean(contextMenuAnchor);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorElement(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorElement(undefined);
    onContextMenuClose?.();
  };

  const handleMerge = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleMenuClose();
    onMerge?.();
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleMenuClose();
    onDelete?.();
  };

  return (
    <GroupNameContainer>
      <BodyText
        sx={{
          fontWeight: 500,
          fontSize: '12px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0,
        }}
      >
        {groupName}
      </BodyText>
      {permissions.canManageReportGroups && (
        <>
          <IconButton className='edit-icon' size='small' onClick={onEdit} sx={{ padding: '2px' }}>
            <EditIcon sx={{ fontSize: '14px' }} />
          </IconButton>
          <IconButton className='menu-icon' size='small' onClick={handleMenuOpen} sx={{ padding: '2px' }}>
            <MoreVertIcon sx={{ fontSize: '14px' }} />
          </IconButton>
          <Menu
            anchorEl={menuAnchorElement}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={contextMenuAnchor ? undefined : { vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={contextMenuAnchor ? undefined : { vertical: 'top', horizontal: 'right' }}
            anchorReference={contextMenuAnchor ? 'anchorPosition' : 'anchorEl'}
            anchorPosition={
              contextMenuAnchor ? { top: contextMenuAnchor.mouseY, left: contextMenuAnchor.mouseX } : undefined
            }
            slotProps={{
              paper: { sx: { borderRadius: '8px', minWidth: '180px' } },
            }}
          >
            <MenuItem onClick={handleMerge} sx={{ py: 1 }}>
              <ListItemIcon>
                <MergeIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText>Merge with another group</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ py: 1 }}>
              <ListItemIcon>
                <DeleteIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText>Delete group</ListItemText>
            </MenuItem>
          </Menu>
        </>
      )}
    </GroupNameContainer>
  );
};
